import React, { useMemo, useRef, useCallback } from "react";
import { TooltipProps } from "recharts";
import { HeatmapRow, formatTime, INT_ORDER } from "../../hooks/ThermalHeatMapHooks";
import { AlertEvent } from "../../hooks/useThermalAlerts";
import { FACE_ORDER } from "../../constants/thermalConstants";
import { ThresholdConfig } from "../panels/ThermalAlertThresholds";
import ChartContent, { FACE_COLORS, getFaceColor } from "./ThermalHeatMapChartContent";
import { GRADIENT_STOPS, tempToHex } from "../../constants/thermalConstants";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";


// ─── Constants ───────────────────────────────────────────────────────────────

const ALL_KEYS: string[] = [...FACE_ORDER, ...INT_ORDER];

const FACE_LABEL: Record<string, string> = {
  PX:   "+X", NX:   "−X",
  PY:   "+Y", NY:   "−Y",
  PZ:   "+Z", NZ:   "−Z",
  INT1: "Deck 1", INT2: "Deck 2",
};

function getFaceLabel(key: string): string {
  const baseKey = key.replace(/_(max|min)$/, '');
  const baseLabel = FACE_LABEL[baseKey] ?? baseKey;
  if (key.endsWith('_max')) return `${baseLabel} (Max)`;
  if (key.endsWith('_min')) return `${baseLabel} (Min)`;
  return baseLabel;
}

function getDisplayLabel(key: string, selectedNode: number | null, visibleFaceCount: number): string {
  let label = getFaceLabel(key);
  if (selectedNode !== null) label += ` Node ${selectedNode}`;
  else if (visibleFaceCount > 1 && !key.match(/_(max|min)$/)) label += ` (Avg)`;
  return label;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface ThermalHeatMapChartProps {
  heatmapRows: HeatmapRow[];
  stepIndex: number;
  nodesPerFace: number;
  thresholds: ThresholdConfig[];
  onToggle3d: () => void;
  maxPoints?: number; // Optional: max chart points for sampling
  alerts?: AlertEvent[]; // Optional: alert events for markers
  selectedNode?: number | null; // If set, show only this node
  selectedBreach?: "high" | "low" | null; // If set, auto-switch metric based on breach type
  isFullscreen?: boolean; // Optional: if true, controls appear on right side
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ThermalHeatMapChartExtraProps {
  visibleFaces?: string[] | null;
  onVisibleFacesChange?: (faces: string[]) => void;
}

const ThermalHeatMapChart: React.FC<ThermalHeatMapChartProps & ThermalHeatMapChartExtraProps> = ({
  heatmapRows,
  stepIndex,
  nodesPerFace,
  thresholds,
  onToggle3d,
  isFullscreen = false,
  visibleFaces: controlledVisibleFaces,
  onVisibleFacesChange,
  maxPoints = 1000,
  alerts = [],
  selectedNode,
  selectedBreach,
}) => {
  const [visibleFaces, setVisibleFaces] = React.useState<Set<string>>(
    () => new Set(ALL_KEYS)
  );
  const [showLegend, setShowLegend] = React.useState(true);
  // Track which line the cursor is hovering so the tooltip shows only that face
  const activeKeyRef = useRef<string | null>(null);

  // Controlled faces logic
  React.useEffect(() => {
    if (controlledVisibleFaces) {
      setVisibleFaces(new Set(controlledVisibleFaces));
    }
  }, [controlledVisibleFaces]);

  const toggleFace = (key: string) => {
    setVisibleFaces((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key); // always keep at least one visible
      } else {
        next.add(key);
      }
      // If controlled, notify parent
      if (onVisibleFacesChange) onVisibleFacesChange(Array.from(next));
      return next;
    });
  };

  // Helper to extract node values (min, max, or avg)
  const getNodeValues = (nodes: Record<string, number>, faceKey: string, mode: 'node' | 'single' | 'multi'): Record<string, number> => {
    const result: Record<string, number> = {};
    if (mode === 'node' && selectedNode !== null) {
      const v = nodes[`${faceKey}_N${selectedNode}`];
      if (v !== undefined) result[faceKey] = v;
    } else if (mode === 'single') {
      let maxVal = -Infinity, minVal = Infinity;
      for (let n = 1; n <= nodesPerFace; n++) {
        const v = nodes[`${faceKey}_N${n}`];
        if (v !== undefined) {
          maxVal = Math.max(maxVal, v);
          minVal = Math.min(minVal, v);
        }
      }
      if (isFinite(maxVal)) result[`${faceKey}_max`] = maxVal;
      if (isFinite(minVal)) result[`${faceKey}_min`] = minVal;
    } else {
      let sum = 0, count = 0;
      for (let n = 1; n <= nodesPerFace; n++) {
        const v = nodes[`${faceKey}_N${n}`];
        if (v !== undefined) { sum += v; count++; }
      }
      if (count > 0) result[faceKey] = sum / count;
    }
    return result;
  };

  const isSingleFace = visibleFaces.size === 1;
  const dataMode = selectedNode !== null ? 'node' : (isSingleFace ? 'single' : 'multi') as 'node' | 'single' | 'multi';

  // ── Build chart data (downsampled to ≤maxPoints points, but always include alert times) ──
  const chartData = useMemo(() => {
    // Downsample, but always include alert times
    const alertTimes = new Set(alerts?.map(a => a.timeSec) ?? []);
    const stride = Math.max(1, Math.floor(heatmapRows.length / maxPoints));
    const sampled = new Set<number>();
    const rows: typeof heatmapRows = [];
    for (let i = 0; i < heatmapRows.length; i++) {
      if (i % stride === 0 || i === heatmapRows.length - 1) {
        rows.push(heatmapRows[i]);
        sampled.add(heatmapRows[i].time_sec);
      }
    }
    // Add any alert rows not already included
    for (const t of Array.from(alertTimes)) {
      const idx = heatmapRows.findIndex(r => r.time_sec === t);
      if (idx !== -1 && !sampled.has(t)) {
        rows.push(heatmapRows[idx]);
      }
    }
    // Sort by time
    rows.sort((a, b) => a.time_sec - b.time_sec);
    
    return rows.map((row) => {
      const point: Record<string, number> = { time: row.time_sec };
      for (const faceKey of ALL_KEYS) {
        Object.assign(point, getNodeValues(row.nodes, faceKey, dataMode));
      }
      return point;
    });
  }, [heatmapRows, nodesPerFace, alerts, maxPoints, visibleFaces, selectedNode, dataMode]);

  const visibleFaceKeys = useMemo(() => {
    const keys = Array.from(visibleFaces);
    return isSingleFace && selectedNode === null ? keys.flatMap(k => [`${k}_max`, `${k}_min`]) : keys;
  }, [visibleFaces, isSingleFace, selectedNode]);

  // ── Breach area bands (vertical shaded regions where any face crosses a threshold) ──
  const breachAreas = useMemo(() => {
    const areas: { x1: number; x2: number; color: string; stroke: string }[] = [];
    if (chartData.length === 0 || visibleFaceKeys.length === 0) return areas;
    for (const t of thresholds) {
      if (!t.enabled) continue;
      // Max breach → red
      if (t.maxC !== null) {
        let start: number | null = null;
        for (const pt of chartData) {
          const breaching = visibleFaceKeys.some(
            (k) => pt[k] !== undefined && pt[k] > t.maxC!
          );
          if (breaching && start === null) {
            start = pt.time;
          } else if (!breaching && start !== null) {
            areas.push({ x1: start, x2: pt.time, color: "rgba(255,107,107,0.18)", stroke: "#ff6b6b" });
            start = null;
          }
        }
        if (start !== null)
          areas.push({ x1: start, x2: chartData[chartData.length - 1].time, color: "rgba(255,107,107,0.18)", stroke: "#ff6b6b" });
      }
      // Min breach → blue
      if (t.minC !== null) {
        let start: number | null = null;
        for (const pt of chartData) {
          const breaching = visibleFaceKeys.some(
            (k) => pt[k] !== undefined && pt[k] < t.minC!
          );
          if (breaching && start === null) {
            start = pt.time;
          } else if (!breaching && start !== null) {
            areas.push({ x1: start, x2: pt.time, color: "rgba(107,197,255,0.18)", stroke: "#6bc5ff" });
            start = null;
          }
        }
        if (start !== null)
          areas.push({ x1: start, x2: chartData[chartData.length - 1].time, color: "rgba(107,197,255,0.18)", stroke: "#6bc5ff" });
      }
    }
    return areas;
  }, [chartData, thresholds, visibleFaceKeys]);

  // ── Threshold reference lines ─────────────────────────────────────────────
  // Assign threshold line colors from the heatmap palette based on value position
  // thresholdLines now includes label and breach type for legend
  const thresholdLines = useMemo(() => {
    const lines: { key: string; y: number; color: string; label: string; breach: 'max' | 'min' }[] = [];
    // Find min/max for normalization
    let min = Infinity, max = -Infinity;
    for (const t of thresholds) {
      if (!t.enabled) continue;
      if (t.maxC !== null) max = Math.max(max, t.maxC);
      if (t.minC !== null) min = Math.min(min, t.minC);
    }
    if (!isFinite(min) || !isFinite(max) || min === max) {
      min = 0; max = 100;
    }
    for (const t of thresholds) {
      if (!t.enabled) continue;
      if (t.maxC !== null) {
        const color = tempToHex(t.maxC, min, max);
        lines.push({ key: `${t.id}_max`, y: t.maxC, color, label: t.name, breach: 'max' });
      }
      if (t.minC !== null) {
        // Use a slightly lighter blue for the lower (min) threshold reference line
        const color = "#89d9ff"; // lighter blue than the default palette
        lines.push({ key: `${t.id}_min`, y: t.minC, color, label: t.name, breach: 'min' });
      }
    }
    return lines;
  }, [thresholds]);

  // Custom tooltip
  const renderTooltip = useCallback((props: TooltipProps<number, string>) => {
    if (!props.active || !props.payload?.length) return null;
    const key = activeKeyRef.current || props.payload[0]?.dataKey;
    const item = props.payload.find((p) => p.dataKey === key) || props.payload[0];
    if (!item) return null;
    const timeSec = (props.payload[0]?.payload as { time: number })?.time ?? 0;
    return (
      <div className="thm-chart-tooltip">
        <div className="thm-chart-tooltip-time">{formatTime(timeSec)}</div>
        <div className="thm-chart-tooltip-row">
          <span className="thm-chart-tooltip-dot" style={{ background: item.color as string }} />
          <span className="thm-chart-tooltip-face">
            {getDisplayLabel(String(item.dataKey), selectedNode ?? null, visibleFaces.size)}
          </span>
          <span className="thm-chart-tooltip-val">
            {(item.value as number)?.toFixed(2)} °C
          </span>
        </div>
      </div>
    );
  }, [selectedNode, visibleFaces.size]);

  // ─────────────────────────────────────────────────────────────────────────
  
  // Memoize UI sections to prevent unnecessary re-renders
  const controlsUI = useMemo(
    () => (
      <div className="thm-chart-controls thm-chart-controls--responsive">
        <div className="thm-chart-face-toggles thm-chart-controls-row">
          {ALL_KEYS.map((key) => (
            <button
              key={key}
              className={`thm-chart-face-pill${visibleFaces.has(key) ? " active" : ""}`}
              style={
                visibleFaces.has(key)
                  ? { borderColor: FACE_COLORS[key], color: FACE_COLORS[key] }
                  : {}
              }
              onClick={() => toggleFace(key)}
            >
              {FACE_LABEL[key]}
            </button>
          ))}
          {visibleFaces.size > 0 && (
            <button
              className="thm-chart-reset-btn"
              onClick={() => setVisibleFaces(new Set())}
            >
              RESET ALL
            </button>
          )}
        </div>
        <div className="thm-chart-metric-btns thm-chart-controls-row">
          <button
            className="thm-chart-legend-toggle-btn"
            onClick={() => setShowLegend((v) => !v)}
            aria-pressed={showLegend}
            type="button"
            style={{ margin: "0 8px" }}
          >
            Legend
          </button>
          <CustomToolTip title="3D View" placement="top">
          <button
            className="thm-chart-metric-btn thm-chart-3d-btn"
            onClick={onToggle3d}
          >
            3D
          </button>
          </CustomToolTip>
        </div>
      </div>
    ),
    [visibleFaces, showLegend, onToggle3d]
  );

  const chartUI = useMemo(
    () => (
      <>
        {visibleFaces.size > 0 ? (
          <>
            <div className="thm-chart-area">
              <ChartContent
                chartData={chartData}
                visibleFaceKeys={visibleFaceKeys}
                thresholdLines={thresholdLines}
                breachAreas={breachAreas}
                renderTooltip={renderTooltip}
                onMouseEnterFace={(key) => { activeKeyRef.current = key; }}
                onMouseLeaveFace={() => { activeKeyRef.current = null; }}
              />
            </div>
            {showLegend && (
              <div className="thm-chart-legend-container">
                <div className="thm-chart-legend-row">
                  {visibleFaceKeys.map((key) => (
                    <span key={key} className="thm-chart-legend-item">
                      <span className="thm-chart-legend-swatch" style={{ background: getFaceColor(key) }} />
                      <span className="thm-chart-legend-label">
                        {getDisplayLabel(key, selectedNode ?? null, visibleFaces.size)}
                      </span>
                    </span>
                  ))}
                  {thresholdLines.map((tl) => (
                    <span key={tl.key} className="thm-chart-legend-item">
                      <span className="thm-chart-legend-swatch" style={{ background: tl.color, border: "1px dashed #888" }} />
                      <span className="thm-chart-legend-label">{tl.label} ({tl.breach === 'max' ? 'Max' : 'Min'})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="thm-chart-empty">
            Select a face or deck to display the chart
          </div>
        )}
      </>
    ),
    [visibleFaces.size, visibleFaceKeys, thresholdLines, showLegend, renderTooltip, selectedNode, chartData]
  );

  return (
    <div className="thm-chart-view">
      {isFullscreen ? (
        // Fullscreen: controls on right side
        <div className="thm-chart-main-row">
          <div className="thm-chart-left">{chartUI}</div>
          {controlsUI}
        </div>
      ) : (
        // Normal: controls on top
        <div className="thm-chart-main-row">
          <div className="thm-chart-left">
            {controlsUI}
            {chartUI}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThermalHeatMapChart;
